import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import dayjs from 'dayjs'

pdfMake.vfs = pdfFonts.pdfMake.vfs

class ServiceOrderReport {
  async call(reportData) {
    const logoBase64 = await this.getCompanyLogo()
    
    const content = [
      { text: reportData.template, style: 'header' },
      { text: `Cliente: ${reportData.client}`, margin: [0, 5] },
      { text: `Placa: ${reportData.plate}`, margin: [0, 5] },
      { text: `Tipo de Tarefa: ${reportData.taskType}`, margin: [0, 5] },
      { text: `Previsão de Início: ${dayjs(reportData.startDate).format("DD/MM/YYYY")}`, margin: [0, 5]},
      { text: `Previsão de Término: ${dayjs(reportData.endDate).format("DD/MM/YYYY")}`, margin: [0, 5]},
      { text: `Relatório Aprovado em: ${dayjs(reportData.approvedDate).format("DD/MM/YYYY [às] HH:mm")}`, margin: [0, 5]},
      { text: `Técnico: ${reportData.technician}`, margin: [0, 5, 0, 15] },
      
      { text: 'Dados do Relatório', style: 'sectionHeader' },
      ...await this.buildReportDataWithImages(reportData.reportData, reportData.formSchema, reportData.images),
    ]

    const docDefinition = {
      pageMargins: [40, 60, 40, 60],
      header: logoBase64 ? {
        columns: [
          { text: `Relatório de Ordem de Serviço Nº${reportData.serviceOrderID}`, alignment: 'center', margin: [80, 20, 0, 0] },
          { image: logoBase64, width: 80, alignment: 'right', margin: [0, 10, 10, 0] }
        ]
      } : { text: `Relatório de Ordem de Serviço Nº${reportData.serviceOrderID}`, alignment: 'center', margin: [0, 20] },
      footer: (currentPage, pageCount) => ({
        text: `Página ${currentPage} de ${pageCount} | ${dayjs().format("DD/MM/YYYY [às] HH:mm")}`,
        alignment: 'center',
        fontSize: 9,
        margin: [0, 20]
      }),
      content,
      styles: {
        header: { fontSize: 16, bold: true, margin: [0, 0, 0, 10] },
        sectionHeader: { fontSize: 12, bold: true, margin: [0, 15, 0, 10] }
      }
    }

    pdfMake.createPdf(docDefinition).download(`relatorio_os_${reportData.serviceOrderID}.pdf`)
  }

  async getCompanyLogo() {
    try {
      const logoUrl = process.env.VUE_APP_IMAGE_LOGIN
      if (!logoUrl) return null
      
      const fullUrl = logoUrl.startsWith('http') ? logoUrl : window.location.origin + logoUrl
      return await this.fetchImageAsBase64(fullUrl)
    } catch (error) {
      console.warn('Logo não pôde ser carregado:', error)
      return null
    }
  }

    async buildReportDataWithImages(reportData, formSchema, images, parentKey = '', level = 0, rootData = null) {
    const content = []
    const properties = formSchema.properties
    const uiSchema = formSchema['ui:order'] || []
    
    if (!rootData) rootData = reportData

    const orderedKeys = uiSchema.length > 0 
      ? uiSchema.filter(key => properties[key])
      : Object.keys(properties)

    for (const key of orderedKeys) {
      const schema = properties[key]
      const fullKey = parentKey ? `${parentKey}.${key}` : key
      
      if (schema.type === 'array') {
        let fieldImages = images?.filter(img => img.formFieldKey === fullKey) || []
        if (fieldImages.length === 0) {
          fieldImages = images?.filter(img => img.formFieldKey === key) || []
        }
        if (fieldImages.length > 0) {
          content.push({ text: schema.title, margin: [level * 10, 10, 0, 5] })
          for (const image of fieldImages) {
            try {
              const imageData = await this.fetchImageAsBase64(image.signURL)
              content.push({ image: imageData, width: 250, margin: [level * 10, 5], alignment: 'center' })
            } catch (error) {
              console.warn('Imagem não pôde ser carregada:', image.signURL)
            }
          }
        }
      } else if (schema.type === 'object') {
        const objectValue = reportData[key]
        if (objectValue) {
          content.push({ text: schema.title, bold: true, margin: [level * 10, 10, 0, 5] })
          const nestedContent = await this.buildReportDataWithImages(objectValue, schema, images, fullKey, level + 1, rootData)
          content.push(...nestedContent)
        }
      } else if (schema.type === 'integer' && schema.readOnly) {
        const value = this.calculateInconformidades(rootData, schema)
        content.push({ text: `${schema.title}: ${value}`, margin: [level * 10, 3] })
      } else {
        const value = reportData[key]
        if (value !== null && value !== undefined) {
          if (typeof value === 'string' && String(value).trim().length === 0) {
            continue
          }
          let displayValue
          
          if (typeof value === 'boolean') {
            displayValue = schema.displayFormat === 'yes_no' 
              ? (value ? 'Sim' : 'Não')
              : (value ? 'Conforme' : 'Inconforme')
          } else if (schema.format === 'date-time') {
            displayValue = dayjs(value).format('DD/MM/YYYY [às] HH:mm')
          } else {
            displayValue = value
          }
          
          const separator = schema.title.endsWith('?') ? ' ' : ': '
          content.push({ text: `${schema.title}${separator}${displayValue}`, margin: [level * 10, 3] })
        }
      }
    }

    return content
  }

  calculateInconformidades(rootData, fieldSchema) {
    if (!fieldSchema || !fieldSchema.calculationSource) {
      return 0;
    }
    
    const sourceSectionKey = fieldSchema.calculationSource;
    
    const checklistData = rootData[sourceSectionKey];
    if (!checklistData || typeof checklistData !== 'object') {
      return 0;
    }
    
    return Object.values(checklistData).filter(v => v === false).length;
  }

  async fetchImageAsBase64(url) {
    const response = await fetch(url, { mode: 'cors' })
    if (!response.ok) throw new Error('Falha ao carregar imagem')

    const blob = await response.blob()

    // 1) Tentativa direta via FileReader
    const tryReadAsDataUrl = () => new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result
        if (typeof result === 'string' && result.startsWith('data:image')) {
          resolve(result)
        } else {
          reject(new Error('dataURL não é image/*'))
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })

    try {
      return await tryReadAsDataUrl()
    } catch (_) {
      try {
        const bitmap = await createImageBitmap(blob)
        const canvas = document.createElement('canvas')
        canvas.width = bitmap.width
        canvas.height = bitmap.height
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(bitmap, 0, 0)
        return canvas.toDataURL('image/jpeg', 0.9)
      } catch (e) {
        throw new Error('Não foi possível converter a imagem recebida')
      }
    }
  }
}

export default ServiceOrderReport
